import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CreateNewFolderRoundedIcon from '@mui/icons-material/CreateNewFolderRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DriveFileMoveRoundedIcon from '@mui/icons-material/DriveFileMoveRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type { AccessGroup } from '@/shared/access';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useAccessGroups, useBackfillAccess, useDeleteAccessGroup } from '../../hooks';
import { descendantsOf, groupsWithChildren, visibleGroupRows } from '../../lib/groupHierarchy';
import { CreateGroupDialog } from '../components/CreateGroupDialog';
import { GroupMembersDialog } from '../components/GroupMembersDialog';
import { MoveGroupDialog } from '../components/MoveGroupDialog';

/**
 * Groups are the intended unit of access.
 *
 * <p>Direct user grants exist, but they are the exception: an access model maintained one person at a time
 * drifts within a quarter and cannot be reviewed. This screen therefore leads with groups and makes
 * membership — not per-user rights — the routine edit.
 *
 * <p>Groups can also nest into a department hierarchy (adr-merchant-portal-016). The BFF returns every
 * group, at every depth, as one FLAT list ordered by `path` — parents precede children and each subtree
 * is contiguous — and this screen renders that as a tree: the name cell indents by `depth`, and any row
 * with children gets an expand/collapse chevron. Collapsing hides the whole subtree; the ordering
 * guarantee (`lib/groupHierarchy.ts`) is what makes that a single filter pass rather than a rebuilt nested
 * structure.
 */
export function GroupsPage() {
  const { t } = useTranslation('access');
  const navigate = useNavigate();
  const groups = useAccessGroups();
  const remove = useDeleteAccessGroup();
  const backfill = useBackfillAccess();

  const [createOpen, setCreateOpen] = useState(false);
  // Preselects the parent when the dialog is opened from a row's "Add sub-department" action;
  // `undefined` (the top "New group" button, or the empty state) creates a top-level group.
  const [createParentId, setCreateParentId] = useState<string | undefined>(undefined);
  const [membersFor, setMembersFor] = useState<AccessGroup | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AccessGroup | null>(null);
  const [moveTarget, setMoveTarget] = useState<AccessGroup | null>(null);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const items = useMemo(() => groups.data ?? [], [groups.data]);
  const withChildren = useMemo(() => groupsWithChildren(items), [items]);
  const visibleRows = useMemo(() => visibleGroupRows(items, collapsedIds), [items, collapsedIds]);
  // DELETE cascades to the whole subtree (Keycloak deletes a group's descendants along with it), so the
  // confirmation must name what else goes — not just the group that was clicked.
  const subDepartments = useMemo(
    () => (confirmDelete ? descendantsOf(items, confirmDelete.id) : []),
    [items, confirmDelete],
  );
  const subDepartmentMemberCount = useMemo(
    () => subDepartments.reduce((sum, group) => sum + group.memberCount, 0),
    [subDepartments],
  );

  const toggleCollapsed = (id: string) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = (parentId?: string) => {
    setCreateParentId(parentId);
    setCreateOpen(true);
  };

  if (groups.isLoading) return <LoadingState label={t('groupsPage.loading')} />;
  if (groups.isError) {
    return (
      <ErrorState
        title={t('groupsPage.loadError')}
        message={groups.error?.message ?? ''}
        onRetry={() => void groups.refetch()}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {t('groupsPage.intro')}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<GroupAddRoundedIcon />}
          onClick={() => openCreate(undefined)}
        >
          {t('groupsPage.create')}
        </Button>
      </Stack>

      {backfill.isSuccess && (
        <Alert severity={backfill.data.skipped ? 'info' : 'success'}>
          {backfill.data.skipped
            ? t('groupsPage.backfill.skipped')
            : t('groupsPage.backfill.done', {
                groups: backfill.data.groupsCreated,
                people: backfill.data.membershipsCreated,
              })}
        </Alert>
      )}
      {backfill.isError && <Alert severity="error">{backfill.error?.message}</Alert>}

      {items.length === 0 ? (
        <EmptyState
          title={t('groupsPage.empty.title')}
          description={t('groupsPage.empty.description')}
          action={
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {/*
                Offered first, and as the primary action. A company arriving here already HAS a working
                access arrangement — the four portal roles — and the migration turns it into something
                editable without changing what anybody can do. Asking them to rebuild it by hand from an
                empty screen would be twenty decisions they never asked to make, and the usual answer to
                twenty decisions is "give everyone everything".
              */}
              <Button
                variant="contained"
                disabled={backfill.isPending}
                onClick={() => backfill.mutate()}
              >
                {t('groupsPage.backfill.action')}
              </Button>
              <Button onClick={() => openCreate(undefined)}>{t('groupsPage.create')}</Button>
            </Stack>
          }
        />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('groupsPage.table.name')}</TableCell>
              <TableCell>{t('groupsPage.table.seededFrom')}</TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                  <span>{t('groupsPage.table.members')}</span>
                  {/* memberCount is DIRECT members only — a sub-department's people are not counted for
                      its parent even though they inherit the parent's access too. */}
                  <Tooltip title={t('groupsPage.table.membersHint')}>
                    <InfoRoundedIcon fontSize="inherit" color="disabled" />
                  </Tooltip>
                </Stack>
              </TableCell>
              <TableCell align="right">{t('groupsPage.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((group) => {
              const depth = group.depth ?? 0;
              const hasChildren = withChildren.has(group.id);
              const isCollapsed = collapsedIds.has(group.id);
              return (
                <TableRow key={group.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', pl: depth * 3 }}>
                      {hasChildren ? (
                        <IconButton
                          size="small"
                          onClick={() => toggleCollapsed(group.id)}
                          aria-expanded={!isCollapsed}
                          aria-label={
                            isCollapsed
                              ? t('groupsPage.tree.expandAria', { name: group.name })
                              : t('groupsPage.tree.collapseAria', { name: group.name })
                          }
                          sx={{ mt: 0.25 }}
                        >
                          {isCollapsed ? (
                            <ChevronRightRoundedIcon fontSize="small" />
                          ) : (
                            <ExpandMoreRoundedIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : (
                        <Box sx={{ width: 32, height: 32, flexShrink: 0 }} aria-hidden />
                      )}
                      <Box sx={{ minWidth: 0, pt: 0.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {group.name}
                          </Typography>
                          {group.builtIn && (
                            <Tooltip title={t('groupsPage.builtInHint')}>
                              <LockRoundedIcon fontSize="inherit" color="disabled" />
                            </Tooltip>
                          )}
                        </Stack>
                        {group.description && (
                          <Typography variant="caption" color="text.secondary">
                            {group.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {group.sourceTemplate ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(`templates.${group.sourceTemplate}.label`, group.sourceTemplate)}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{group.memberCount}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('groupsPage.actions.editAccess')}>
                      <IconButton
                        size="small"
                        aria-label={t('groupsPage.actions.editAccessAria', { name: group.name })}
                        onClick={() =>
                          navigate(`/team/access?subjectType=GROUP&subjectId=${group.id}`)
                        }
                      >
                        <TuneRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        group.builtIn
                          ? t('groupsPage.builtInHint')
                          : t('groupsPage.actions.members')
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={group.builtIn}
                          aria-label={t('groupsPage.actions.membersAria', { name: group.name })}
                          onClick={() => setMembersFor(group)}
                        >
                          <GroupAddRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('groupsPage.actions.addSubDepartment')}>
                      <IconButton
                        size="small"
                        aria-label={t('groupsPage.actions.addSubDepartmentAria', {
                          name: group.name,
                        })}
                        onClick={() => openCreate(group.id)}
                      >
                        <CreateNewFolderRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        group.builtIn ? t('groupsPage.builtInHint') : t('groupsPage.actions.move')
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={group.builtIn}
                          aria-label={t('groupsPage.actions.moveAria', { name: group.name })}
                          onClick={() => setMoveTarget(group)}
                        >
                          <DriveFileMoveRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        group.builtIn ? t('groupsPage.builtInHint') : t('groupsPage.actions.delete')
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={group.builtIn}
                          aria-label={t('groupsPage.actions.deleteAria', { name: group.name })}
                          onClick={() => setConfirmDelete(group)}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <CreateGroupDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateParentId(undefined);
        }}
        initialParentId={createParentId}
      />
      <GroupMembersDialog group={membersFor} onClose={() => setMembersFor(null)} />
      <MoveGroupDialog group={moveTarget} onClose={() => setMoveTarget(null)} />

      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t('groupsPage.confirmDelete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {subDepartments.length > 0
              ? t('groupsPage.confirmDelete.bodyWithSubDepartments', {
                  name: confirmDelete?.name ?? '',
                  subCount: subDepartments.length,
                  totalMembers: (confirmDelete?.memberCount ?? 0) + subDepartmentMemberCount,
                })
              : t('groupsPage.confirmDelete.body', {
                  name: confirmDelete?.name ?? '',
                  count: confirmDelete?.memberCount ?? 0,
                })}
          </DialogContentText>
          {subDepartments.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('groupsPage.confirmDelete.subDepartmentsWarning')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (!confirmDelete) return;
              remove.mutate(confirmDelete.id, { onSettled: () => setConfirmDelete(null) });
            }}
          >
            {t('groupsPage.confirmDelete.action')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
